import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { StickyHeaderNavigation, OTPInput } from '@/components'
import { useVerifyPhoneUpdate } from '@/domain/identity/useCases'
import { useProfileUpdateStore } from '@/store/profileUpdate'

const CELL_COUNT = 4
const defaultCodes = Array(CELL_COUNT).fill('')

export const NewPhoneValidateCodeScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [codes, setCodes] = useState<string[]>(defaultCodes)
  const [errorMessage, setErrorMessage] = useState('')

  const { newPhoneNumber } = useProfileUpdateStore()

  const { mutate: verifyPhoneUpdate, isPending: isVerifying } =
    useVerifyPhoneUpdate({
      onSuccess: () => {
        navigate('/successful-profile-update')
      },
      onError: ({ errorMessage: errMsg, code: errCode }) => {
        if (errCode === 'TOO_MANY_ATTEMPTS' && errMsg) {
          setCodes(defaultCodes)
          setErrorMessage(errMsg)
          return
        }

        if (errCode === 'PHONE_NUMBER_ALREADY_EXISTS_ERROR' && errMsg) {
          setCodes(defaultCodes)
          setErrorMessage(errMsg)
          return
        }

        setErrorMessage(errMsg || t('api:validationErrors.invalidCode'))
        setCodes(defaultCodes)
      },
    })

  const handleSubmit = (fullCode: string) => {
    verifyPhoneUpdate({
      code: fullCode,
      phone: newPhoneNumber || '',
      new_phone: true,
    })
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  return (
    <BaseView flex={1} bgColor="background.screen" className="h-full">
      <StickyHeaderNavigation
        title={t('screens:editPhoneNumber.verifyNewPhone')}
        onBackPress={handleGoBack}
      />

      <BaseView
        flex={1}
        px={12}
        style={{
          overflowY: 'auto',
        }}
      >
        <BaseView gap={24}>
          <BaseView gap={8}>
            <BaseText fontSize={18} fontFamily="semibold" color="label.default">
              {t('screens:auth.enterVerificationCode')}
            </BaseText>
            <BaseText
              fontSize={14}
              fontFamily="regular"
              color="label.description"
            >
              {t('screens:editPhoneNumber.weSentCodeToYourNewPhone')}{' '}
              {newPhoneNumber}
            </BaseText>
          </BaseView>

          <BaseView style={{ alignItems: 'center' }}>
            <OTPInput
              codes={codes}
              setCodes={setCodes}
              onSubmit={handleSubmit}
              isSubmitting={isVerifying}
              errorMessage={errorMessage}
              setErrorMessage={setErrorMessage}
              autoFocus
              autoFocusAfterClean
              numberCells={CELL_COUNT}
            />
          </BaseView>
        </BaseView>
      </BaseView>
    </BaseView>
  )
}

export default NewPhoneValidateCodeScreen
